import { Routes, Route, Navigate } from 'react-router-dom'
import { useState, useEffect, lazy, Suspense } from 'react'
import { useAuth, AuthProvider } from './contexts/AuthContext'
import { usePermissions } from './hooks/usePermissions'
import { LoadingProvider } from './contexts/LoadingContext'
import { CallStateProvider } from './contexts/CallStateContext'
import { CampaignProvider } from './contexts/CampaignContext'
import { TelephonyProvider } from './telephony/TelephonyProvider'
import { useGlobalLoading } from './hooks/useGlobalLoading'
import AppShell from './components/AppShell'
// ✅ GlobalLoader removed - was causing latency at login/dashboard load
import ProtectedRoute from './components/ProtectedRoute'
import BillingRoute from './components/BillingRoute'
import { SmartImportGate } from './components/contact-import/SmartImportGate'
// L-NAV-4 — Intégrations et AlloDons sont des sections de /settings (console).
import { SettingsSectionRedirect } from './pages/settings/SettingsSectionRedirect'
import ForcePasswordChangeModal from './components/modals/ForcePasswordChangeModal'
import { ErrorBoundary } from './components/ErrorBoundary'
import { ProfileErrorScreen } from './components/ProfileErrorScreen'
import { OfflineDetector } from './components/OfflineDetector'
import { ServiceWorkerUpdateBanner } from './pwa/ServiceWorkerUpdateBanner'
import { logger } from './utils/logger'
import { publicOnlyRedirects } from './routes/publicOnlyRedirects'
import { legacyStatsRedirects } from './routes/legacyStatsRedirects'
import { legacyMergeRedirects } from './routes/legacyMergeRedirects'
import { legacyCampaignAdminRedirects } from './routes/legacyCampaignAdminRedirects' // L-NAV-3
// L-NAV-5
import { legacyHistoryRedirects } from './routes/legacyHistoryRedirects'
import { RedirectToLogin, ReturnToTracker } from './routes/loginReturn'
// L-NAV-6 — l'Historique donateur est devenu l'onglet « Donateurs sans fiche » de Contacts.
import { DonorHistoryRedirect } from './routes/donorHistoryRedirect'
// L-NAV-7 — « Équipe » : /admin/users et /admin/permissions redirigent vers /equipe.
import { legacyTeamRedirects } from './routes/legacyTeamRedirects'
// L-NAV-8 — « Aujourd'hui » (/accueil) remplace /supervision ; accueil par rôle.
import { legacyTodayRedirects } from './routes/legacyTodayRedirects'
import { loginHomeFor, roleHome } from './routes/roleHome'
// fin L-NAV-8

// ℹ️ QueryClient/QueryClientProvider, la pile de notifications de repli
// (`FloatingToastStack`) et ReactQueryDevtools sont montés UNE SEULE FOIS dans
// `main.tsx`. Ils étaient auparavant montés en double (ici + main), ce qui
// donnait deux caches React Query et deux conteneurs de toasts superposés.
// LOT-H9 : la pile principale, elle, vit dans le flux de l'`AppShell`
// (`NotificationRegion`) — et le repli s'efface tant qu'elle est montée.

// Lazy load pages pour code splitting
const LoginPage = lazy(() => import('./pages/LoginPage'))
const RegisterPage = lazy(() => import('./pages/RegisterPage'))
const ContactsPage = lazy(() => import('./pages/ContactsPage'))
const ContactTimelinePage = lazy(() => import('./pages/ContactTimelinePage'))
const CampaignsPage = lazy(() => import('./pages/CampaignsPage'))
const CampaignDetailPage = lazy(() => import('./pages/CampaignDetailPage'))
const MyCampaignsPage = lazy(() => import('./pages/MyCampaignsPage'))
// SMS-CAMPAGNE-2 — campagnes SMS (liste = onglet SMS de Campagnes, nouvelle, suivi).
const SmsCampaignsListPage = lazy(() => import('./pages/sms-campaigns/SmsCampaignsListPage'))
const NewSmsCampaignPage = lazy(() => import('./pages/sms-campaigns/NewSmsCampaignPage'))
const SmsCampaignTrackingPage = lazy(() => import('./pages/sms-campaigns/SmsCampaignTrackingPage'))
const ImportPage = lazy(() => import('./pages/ImportPageNEW'))
const ContactImportPage = lazy(() => import('./pages/ContactImportPage'))
// L-NAV-5 — les 4 journaux (appels, enregistrements, SMS, e-mails) sont les
// onglets de la page « Historique » ; leurs anciens chemins redirigent.
const HistoryPage = lazy(() => import('./pages/history/HistoryPage'))
const SoftphoneV2Page = lazy(() => import('./telephony/pages/SoftphoneV2Page'))
// LOT-H12-ANALYTICS : 4 pages de statistiques → 2 (recette DA §5).
const AnalyticsPage = lazy(() => import('./pages/analytics/AnalyticsPage'))
// L-NAV-8 — /supervision (temps réel à onglets) devient l'accueil « Aujourd'hui ».
const TodayPage = lazy(() => import('./pages/today/TodayPage'))
// LOT-H12-DOUBLONS : /admin/team + /admin/all-users → /admin/users (DA-032) ;
// modèles et fournisseurs SMS/E-mail → pages à onglets /messaging/* (DA-047).
const UsersPage = lazy(() => import('./pages/admin/UsersPage'))
// L-NAV-7 — fiche d'une personne (Compte · Téléphone · Accès · Campagnes · AlloDons).
const PersonPage = lazy(() => import('./pages/team/PersonPage'))
const TenantsPage = lazy(() => import('./pages/TenantsPage'))
const TenantDetailsPage = lazy(() => import('./pages/TenantDetailsPage'))
const TwilioNumbersPage = lazy(() => import('./pages/TwilioNumbersPage'))
// L-NAV-7 — PermissionsPage supprimée : ses droits sont la section « Accès »
// de la fiche `/equipe/:userId` ; `/admin/permissions` redirige.
// L-NAV-3 : les pages admin « réglages » et « assignations » d'une campagne
// sont fondues dans les onglets de /campaigns/:id (redirections plus bas).
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
// #245 — retour public du paiement AlloDons (success_url / cancel_url).
const DonationThanksPage = lazy(() => import('./pages/DonationThanksPage'))
const TemplatesPage = lazy(() => import('./pages/messaging/TemplatesPage'))
const ProvidersPage = lazy(() => import('./pages/messaging/ProvidersPage'))
const UpgradePage = lazy(() => import('./pages/UpgradePage'))
const UpgradeSuccessPage = lazy(() => import('./pages/UpgradeSuccessPage'))
const UpgradeCancelPage = lazy(() => import('./pages/UpgradeCancelPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-900">
    <div className="flex flex-col items-center space-y-4">
      <div className="w-12 h-12 border-4 border-slate-600 rounded-full animate-spin border-t-primary-500"></div>
      <p className="text-white font-medium">Chargement...</p>
    </div>
  </div>
)

/**
 * Atterrissage de la racine `/`, par rôle (décision UX D-1).
 *
 * L'AGENT enchaîne des appels toute la journée : sa page d'accueil est son OUTIL
 * (`/softphone`), pas un tableau de bord passif. Les autres rôles (ADMIN, SUPERADMIN,
 * USER) gardent `/dashboard-analytics`.
 *
 * ⚠️ La redirection est aussi conditionnée à la PERMISSION, pas au seul rôle :
 * `/softphone` est protégé par `accessDialer`, donc envoyer un AGENT qui ne l'a pas
 * l'amènerait sur UnauthorizedPage — une régression. Sans la permission, on retombe
 * sur le tableau de bord.
 */
function RoleLanding() {
  const { user, can, hasPermission } = usePermissions()

  // LOT-H12 : la LOGIQUE post-login ne change pas — même aiguillage par rôle ;
  // seule la cible « tableau de bord » suit la fusion /dashboard-analytics → /analytics.
  // LOT-SUNSET-2 : la cible dialer est la scène v2 en direct (la v1 n'existe plus).
  // L-NAV-8 : l'ADMIN atterrit sur « Aujourd'hui » (/accueil) — `routes/roleHome.ts`.
  // Recette 3B : sans `canAccessStats`, ni /accueil ni /analytics ne sont permis ⇒ repli.
  return <Navigate to={roleHome(user?.role, Boolean(can.accessDialer), hasPermission('canAccessStats'))} replace />
}

function AppContent() {
  const { user, isLoading, refreshProfile, profileError, retryProfile, logout } = useAuth()
  // Recette 3B — l'accueil post-connexion dépend de `canAccessStats` (routes/roleHome.ts).
  const { hasPermission: hasPermissionForHome } = usePermissions()
  const [showForcePasswordChange, setShowForcePasswordChange] = useState(false)

  // Initialize global loading connection
  useGlobalLoading()

  // LOT-MODAL-MDP : le drapeau SUIT `user.mustChangePassword` dans les deux
  // sens. L'ancien effet ne faisait que l'armer (jamais `false`) : après un
  // « Se déconnecter » depuis le modal puis une connexion sur un AUTRE compte
  // sain, l'état résiduel `true` aurait ré-affiché le modal au mauvais
  // utilisateur.
  useEffect(() => {
    setShowForcePasswordChange(Boolean(user?.mustChangePassword))
  }, [user])

  const handlePasswordChangeSuccess = async () => {
    // Fermeture immédiate (le refresh est asynchrone), puis `refreshProfile()`
    // de l'AuthContext (#96) recharge le profil — `mustChangePassword` revient
    // à false et l'effet ci-dessus entérine la fermeture. Remplace le
    // `window.location.reload()` historique : plus de rechargement complet de
    // la PWA. En cas d'échec réseau du refresh, `refreshProfile` ne jette pas
    // (il journalise) ; le drapeau local reste périmé jusqu'au prochain
    // rafraîchissement, sans ré-ouvrir le modal.
    setShowForcePasswordChange(false)
    await refreshProfile()
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="bg-slate-800/80 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-slate-700">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-12 h-12 border-4 border-slate-600 rounded-full animate-spin border-t-primary-500"></div>
            <p className="text-white font-medium">Chargement de l'application...</p>
          </div>
        </div>
      </div>
    )
  }

  // #194 (D3) : profil injoignable (429, réseau, 5xx) — la session est gardée,
  // on propose de réessayer au lieu de renvoyer à la page de connexion.
  if (profileError && !user) {
    return <ProfileErrorScreen onRetry={retryProfile} onLogout={logout} />
  }

  if (!user) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          {/* #245 — le donateur revient de la page AlloDons SANS compte : page publique. */}
          <Route path="/don/merci" element={<DonationThanksPage />} />
          <Route path="*" element={<RedirectToLogin />} />
        </Routes>
      </Suspense>
    )
  }

  logger.debug('👤 App: User authenticated:', user.email)
  return (
    <>
      {/* #262 — page courante retenue pour le retour après reconnexion. */}
      <ReturnToTracker userId={user.id} />
      <AppShell>
        <Suspense fallback={<PageLoader />}>
          <Routes>
          {/* L-NAV-9 — Analyse (rétrospectif) : canAccessStats, comme au menu
              (l'agent y voit SES données ; 100 % des comptes de prod l'ont). */}
          <Route
            path="/analytics"
            element={
              <ProtectedRoute screen="analytics">
                <AnalyticsPage />
              </ProtectedRoute>
            }
          />
          {/* L-NAV-8 — « Aujourd'hui » (temps réel + à traiter) — L-NAV-9 : canAccessStats + ADMIN
              (écran `today` du catalogue). L'ancien /supervision y redirige (requête conservée, plus bas). */}
          <Route
            path="/accueil"
            element={
              <ProtectedRoute screen="today">
                <TodayPage />
              </ProtectedRoute>
            }
          />
          {/* fin L-NAV-8 / L-NAV-9 */}
          
          {/* Dialer - ADMIN et AGENT uniquement.
              LOT-SUNSET-2 : les pages v1 (/softphone → NewSoftphonePage,
              /simple-dialer → SimpleDialerPage, /incoming-call/:callSid →
              IncomingCallPage) sont SUPPRIMÉES. Les chemins restent en
              REDIRECTION directe vers la scène v2 : favoris, liens partagés,
              `navigate('/softphone')` d'un vieux bundle PWA et historique
              navigateur ne cassent pas. */}
          <Route path="/simple-dialer" element={<Navigate to="/v2/softphone" replace />} />
          <Route path="/softphone" element={<Navigate to="/v2/softphone" replace />} />
          <Route path="/incoming-call/:callSid" element={<Navigate to="/v2/softphone" replace />} />
          {/* Téléphonie v2 — la SEULE surface d'appel du produit. */}
          <Route
            path="/v2/softphone"
            element={
              <ProtectedRoute requiredPermission="accessDialer">
                <SoftphoneV2Page />
              </ProtectedRoute>
            }
          />

          {/* Pages accessibles à tous les utilisateurs authentifiés */}
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/contacts/:contactId/timeline" element={<ContactTimelinePage />} />
          
          {/* Billing & Upgrade (LOT-UPGRADE) — derrière l'interrupteur
              billing_enabled : éteint (défaut) les trois routes affichent
              « Offre non activée » ; allumé, le tunnel est réservé aux ADMIN.
              La garde fautive qui rendait /upgrade inatteignable (redirection
              rôle !== USER dans la page) vit désormais ici, dans BillingRoute. */}
          <Route
            path="/upgrade"
            element={
              <BillingRoute>
                <UpgradePage />
              </BillingRoute>
            }
          />
          <Route
            path="/upgrade/success"
            element={
              <BillingRoute>
                <UpgradeSuccessPage />
              </BillingRoute>
            }
          />
          <Route
            path="/upgrade/cancel"
            element={
              <BillingRoute>
                <UpgradeCancelPage />
              </BillingRoute>
            }
          />
          
          {/* L-NAV-9 — Campagnes : canAccessCampaigns et pas AGENT (la condition
              du menu, inchangée) — avant : canEditCampaigns ‖ canCreateCampaigns. */}
          <Route 
            path="/campaigns" 
            element={
              <ProtectedRoute screen="campaigns">
                <CampaignsPage />
              </ProtectedRoute>
            } 
          />
          {/* SMS-CAMPAGNE-2 — campagnes SMS : ADMIN du tenant (et compte
              système, qui désigne l'organisation). Chemins statiques : ils
              l'emportent sur `/campaigns/:id` quel que soit l'ordre. */}
          <Route
            path="/campaigns/sms"
            element={
              <ProtectedRoute screen="smsCampaigns">
                <SmsCampaignsListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/campaigns/sms/new"
            element={
              <ProtectedRoute screen="smsCampaigns">
                <NewSmsCampaignPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/campaigns/sms/:id"
            element={
              <ProtectedRoute screen="smsCampaigns">
                <SmsCampaignTrackingPage />
              </ProtectedRoute>
            }
          />
          {/* #451 (planche 15) — la FICHE s'ouvre aussi aux agents (lecture
              seule, campagnes affectées : garde serveur) ; leur liste est
              « Mes campagnes ». */}
          <Route 
            path="/campaigns/:id" 
            element={
              <ProtectedRoute screen="campaignDetail">
                <CampaignDetailPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/mes-campagnes"
            element={
              <ProtectedRoute screen="myCampaigns">
                <MyCampaignsPage />
              </ProtectedRoute>
            }
          />
          {/* fin L-NAV-9 */}
          {/* L-NAV-3 : /admin/campaigns/:id/settings et /assignments redirigent
              vers les onglets Paramètres et Équipe de /campaigns/:id. */}
          {legacyCampaignAdminRedirects()}
          
          {/* Import - Available for USER role with canAccessImport */}
          <Route 
            path="/import" 
            element={
              <ProtectedRoute requiredPermission="canAccessImport">
                <ImportPage />
              </ProtectedRoute>
            } 
          />
          {/* #208 — import intelligent : OPTION par client (décision client
              2026-10-06), éteinte par défaut. `/import` reste l'ancien écran
              pour tout le monde ; cette route ne s'ouvre qu'aux administrateurs
              d'un client dont l'option est active, et renvoie vers `/import`
              avec un message sinon (`SmartImportGate`, testée). */}
          <Route
            path="/import/intelligent"
            element={
              <SmartImportGate>
                <ContactImportPage />
              </SmartImportGate>
            }
          />
          
          {/* L-NAV-5 — « Historique » : une page, 4 onglets (?tab=calls |
              recordings | sms | emails). /calls, /recordings, /sms/logs et
              /emails/logs redirigent en gardant leur requête.
              L-NAV-9 — route : canAccessCalls (comme le menu) ; chaque onglet
              lit SON drapeau dans la page (`historyTabs.ts`). */}
          <Route
            path="/historique"
            element={
              <ProtectedRoute screen="history">
                <HistoryPage />
              </ProtectedRoute>
            }
          />
          {legacyHistoryRedirects()}
          {/* fin L-NAV-5 */}
          <Route path="/settings" element={<SettingsPage />} />
          {/* L-NAV-4 — console des paramètres : « Intégrations » (#270) et
              « AlloDons » (LOT-ALLODONS-2) sont des SECTIONS de /settings. Les
              anciennes adresses redirigent en conservant leur requête
              (`?connectionId=`) et leur état (motif d'intégration). La garde
              ADMIN de ces deux routes vit désormais dans la console
              (`organisationSections`, même règle : role === 'ADMIN'). */}
          <Route path="/settings/integrations" element={<SettingsSectionRedirect section="integrations" />} />
          <Route path="/settings/allodons" element={<SettingsSectionRedirect section="allodons" />} />
          {/* fin L-NAV-4 */}
          {/* L-NAV-6 — « un donateur est un contact » : la page Historique
              donateur n'existe plus. Son onglet « Donateurs sans fiche » vit
              dans Contacts (`?tab=donors`, gardé par AlloDons raccordé), sa
              recherche est celle de Contacts. L'ancien chemin REDIRIGE,
              requête conservée (`DonorHistoryRedirect`, testée). */}
          <Route path="/donations/history" element={<DonorHistoryRedirect />} />
          {/* #245 — même page de retour pour un poste connecté (agent qui teste le lien). */}
          <Route path="/don/merci" element={<DonationThanksPage />} />

          {/* L-NAV-7 — « Équipe » (ADMIN, garde des anciennes routes) : la liste
              (page Utilisateurs fusionnée LOT-H12-DOUBLONS — ADMIN voit son
              tenant, SUPER-ADMIN gagne le sélecteur « Client ») et la fiche
              d'une personne. /admin/users et /admin/permissions redirigent
              (requête et état conservés, `legacyTeamRedirects`). */}
          <Route
            path="/equipe"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <UsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/equipe/:userId"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <PersonPage />
              </ProtectedRoute>
            }
          />
          {legacyTeamRedirects()}
          {/* fin L-NAV-7 */}

          {/* L-NAV-9 — groupe « Système » : compte système, sans dépendre d'un
              drapeau (avant : canViewAllTenants, que seul le super-admin a). */}
          <Route 
            path="/admin/tenants/:id" 
            element={
              <ProtectedRoute screen="tenants">
                <TenantDetailsPage />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/admin/tenants" 
            element={
              <ProtectedRoute screen="tenants">
                <TenantsPage />
              </ProtectedRoute>
            } 
          />
          
          <Route
            path="/admin/twilio-numbers"
            element={
              <ProtectedRoute screen="twilioNumbers">
                <TwilioNumbersPage />
              </ProtectedRoute>
            } 
          />
          {/* fin L-NAV-9 */}
          
          {/* Messagerie fusionnée (LOT-H12-DOUBLONS) : une page « Modèles » et
              une page « Fournisseurs », à onglets SMS | E-mail. Les 4 anciennes
              routes redirigent (voir plus bas).
              L-NAV-9 — Modèles : canAccessSmsTemplates ‖ canAccessEmailTemplates
              (compat canManagePermissions), chaque onglet lit le sien ;
              Fournisseurs : compte système (comme le menu — avant : rôle ADMIN,
              un ADMIN de client y arrivait par l'URL sans entrée de menu). */}
          <Route
            path="/messaging/templates"
            element={
              <ProtectedRoute screen="templates">
                <TemplatesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messaging/providers"
            element={
              <ProtectedRoute screen="providers">
                <ProvidersPage />
              </ProtectedRoute>
            }
          />
          {/* fin L-NAV-9 */}

          
          {/* LOT-SUNSET-2 : /admin/telephony-lab (diagnostic v1) est supprimé
              SANS redirection — outil interne ADMIN, aucun lien vivant. */}

          {/* Redirections */}
          <Route path="/" element={<RoleLanding />} />
          {/* LOT-H12 : les 4 anciennes pages de stats redirigent vers leurs
              fusions — signets, liens partagés et historique ne cassent pas. */}
          {legacyStatsRedirects()}
          {/* LOT-H12-DOUBLONS : les 6 anciennes pages fusionnées (équipe,
              tous-les-utilisateurs, modèles et fournisseurs SMS/E-mail)
              redirigent — même décision. */}
          {legacyMergeRedirects()}
          {/* L-NAV-8 — /supervision → /accueil (requête conservée). */}
          {legacyTodayRedirects()}
          {/* `/login` et `/register` n'existent que dans l'arbre NON authentifié :
              sans ces deux routes, un utilisateur connecté qui les visite tombe
              sur le `*` ci-dessous, donc sur la 404 (DA-035). Elles doivent
              rester AVANT lui. */}
          {/* L-NAV-8 : l'admin va droit sur /accueil après connexion. */}
          {publicOnlyRedirects(user.id, loginHomeFor(user.role, hasPermissionForHome('canAccessStats')))}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
      </AppShell>
      {/* ✅ GlobalLoader removed - was causing latency at login/dashboard load */}
      
      {/* Force Password Change Modal */}
      {showForcePasswordChange && (
        <ForcePasswordChangeModal onSuccess={handlePasswordChangeSuccess} />
      )}
    </>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <LoadingProvider>
          <CampaignProvider>
            <CallStateProvider>
              <TelephonyProvider>
                {/* LOT-BLINDAGE (B1) — boundary INTERNE, SOUS TelephonyProvider.
                    Chaîne causale prouvée en prod : un throw de rendu n'importe
                    où (typiquement React.lazy qui rejette sur un chunk purgé
                    par un déploiement) remontait jusqu'au boundary RACINE,
                    au-dessus du provider → démontage → cleanup →
                    `telephonyDevice.destroy()` → l'appel ÉTABLI était raccroché
                    par le navigateur (« who hung up: caller » chez Twilio).
                    Ce boundary-ci capte le crash SANS démonter le provider :
                    l'écran de repli s'affiche, le Device survit, l'appel en
                    cours continue. Le boundary racine reste en dernier recours
                    (crash du provider lui-même ou d'un contexte). */}
                <ErrorBoundary>
                  <OfflineDetector />
                  {/* LOT-SW-REPOS — publie « mise à jour prête, appliquée à la
                      fin de l'appel » dans la zone de notification quand une
                      MAJ du service worker attend que le poste soit au repos.
                      Ne rend rien lui-même ; rien au repos (application
                      silencieuse). */}
                  <ServiceWorkerUpdateBanner />
                  <AppContent />
                </ErrorBoundary>
              </TelephonyProvider>
            </CallStateProvider>
          </CampaignProvider>
        </LoadingProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App
